import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { useOfferStore } from '@/lib/store';
import {
  ermittleEffektivenBatchAufschlag,
  formatBatchAufschlagHinweis,
} from '@shared/catalog-batch-suggest';
import {
  applyX84DirectUnitPrices,
  canCommitX84ParseResult,
  commitOfferDocumentWriteIfCurrent,
  formatX84DirectApplyDescription,
  formatX84MaterialApplyDescription,
  planX84MaterialEkAttempt,
  selectSuccessfullyAppliedValues,
  transitionX84MaterialEkApply,
} from '@shared/batch-apply-ui-controller';
import {
  matchGaebPrices,
  type GaebPriceMatch,
  type ImportedPriceEntry,
  type OfferPositionForMatch,
} from '@shared/gaeb-price-match';
import {
  BLITZ_ZEILEN_IDS,
  einheitspreisAusKalkulation,
  hatKalkulation,
  materialEkImportBlockiertDurchBestandskalkulation,
  mengenBezugFuerPreisImport,
  rundetStillAufNull,
  setzeBlitzZeile,
} from '@shared/kalkulation';
import type { Position, PositionsKalkulation } from '@shared/schema';
import { AlertTriangle, FileUp, Loader2, Upload } from 'lucide-react';

type ImportMode = 'material-ek' | 'direkt-ep';

interface ParsedImport {
  fileName: string;
  entries: ImportedPriceEntry[];
  offerDocumentEpoch: number;
}

function formatEuro(value: number): string {
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * X84-Preisrückimport vom Großhandel:
 * Liest eine X84/X83-Datei mit Einheitspreisen ein, matcht sie gegen die
 * geladenen Positionen (GAEB-ID bzw. normalisierte Positionsnummer) und
 * übernimmt die Preise wahlweise als Material-EK in die Kalkulation
 * (mit Standard-Materialaufschlag) oder direkt als Einheitspreis.
 *
 * Eigenständige Button+Dialog-Komponente. Seit Welle 1.3 der UX-Entlastung
 * wahlweise als CONTROLLED Geschwister-Dialog im "Export ▾"-Menü der
 * Angebots-Sidebar (Props `open`/`onOpenChange`) — der Menü-Eintrag lebt dann
 * im Parent und lässt das Dropdown normal schließen. Ohne `open` (Default)
 * bleibt der eigenständige Trigger-Button + interner Open-State erhalten.
 */
interface X84PriceImportProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function X84PriceImport({ open: openProp, onOpenChange: onOpenChangeProp }: X84PriceImportProps = {}) {
  const positions = useOfferStore((s) => s.positions);
  const quoteItems = useOfferStore((s) => s.quoteItems);
  const kalkulationsDefaults = useOfferStore((s) => s.kalkulationsDefaults);
  const updatePosition = useOfferStore((s) => s.updatePosition);
  const updatePositionKalkulation = useOfferStore((s) => s.updatePositionKalkulation);
  const offerDocumentEpoch = useOfferStore((s) => s.offerDocumentEpoch);
  const { toast } = useToast();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const parseGenerationRef = useRef(0);
  const parseAbortControllerRef = useRef<AbortController | null>(null);
  const lastProcessedOfferDocumentEpochRef = useRef(offerDocumentEpoch);
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const isOpen = isControlled ? openProp : internalOpen;
  const setIsOpen = (open: boolean) => {
    if (isControlled) onOpenChangeProp?.(open);
    else setInternalOpen(open);
  };
  const [isParsing, setIsParsing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [parsed, setParsed] = useState<ParsedImport | null>(null);
  const [mode, setMode] = useState<ImportMode>('material-ek');
  const [appliedMaterialEkIds, setAppliedMaterialEkIds] = useState<Set<number>>(new Set());
  const [rejectedMaterialEkIds, setRejectedMaterialEkIds] = useState<Set<number>>(new Set());

  // Nur echte Positionen (keine Titel-/Header-Zeilen) matchen
  const matchablePositions = useMemo<OfferPositionForMatch[]>(
    () =>
      positions
        .filter((p) => (p as any).type !== 'title')
        .map((p) => ({
          id: p.id,
          positionNumber: (p as any).positionNumber ?? null,
          originalId: p.originalId ?? null,
          gaebOriginalId: (p as any).gaebOriginalId ?? null,
          name: p.name ?? null,
        })),
    [positions],
  );

  const matchResult = useMemo(
    () => (parsed ? matchGaebPrices(matchablePositions, parsed.entries) : null),
    [parsed, matchablePositions],
  );

  const positionById = useMemo(() => {
    const map = new Map<number, Position>();
    for (const p of positions) map.set(p.id, p);
    return map;
  }, [positions]);

  const mengeById = useMemo(() => {
    const map = new Map<number, number>();
    for (const item of quoteItems) {
      const menge = Number(item.menge);
      map.set(item.id, Number.isFinite(menge) && menge > 0 ? menge : 1);
    }
    return map;
  }, [quoteItems]);

  /** Baut die neue Kalkulation mit Material-EK-Blitzzeile (Beschreibung = Dateiname). */
  const buildKalkulationMitMaterialEk = (
    position: Position,
    unitPrice: number,
    quelle: string,
  ): PositionsKalkulation => {
    // EP-Bug-Fix: der importierte X84-EP ist inhärent PRO EINHEIT, nicht für die Gesamtmenge —
    // setzeBlitzZeile setzt bei einer brandneuen Kalkulation aber 'gesamt' (Blitzkalkulations-
    // Semantik). hatKalkulation() MUSS auf dem Stand VOR setzeBlitzZeile geprüft werden, siehe
    // shared/kalkulation.ts::mengenBezugFuerPreisImport.
    const hatteVorherKalkulation = hatKalkulation(position.kalkulation ?? null);
    const basis = setzeBlitzZeile(position.kalkulation ?? null, 'material', { einzelpreis: unitPrice });
    const mitBeschreibung: PositionsKalkulation = {
      ...basis,
      zeilen: basis.zeilen.map((zeile) =>
        zeile.id === BLITZ_ZEILEN_IDS.material
          ? { ...zeile, beschreibung: `Großhandel: ${quelle}` }
          : zeile,
      ),
    };
    return mengenBezugFuerPreisImport(mitBeschreibung, hatteVorherKalkulation);
  };

  /** Neuer Einheitspreis für die Vorschau, abhängig vom gewählten Modus. */
  const neuerEinheitspreis = (match: GaebPriceMatch): number | null => {
    if (mode === 'direkt-ep') return match.unitPrice;
    const position = positionById.get(match.positionId);
    if (!position) return null;
    const kalkulation = buildKalkulationMitMaterialEk(position, match.unitPrice, parsed?.fileName ?? 'X84');
    const menge = mengeById.get(match.positionId) ?? 1;
    return einheitspreisAusKalkulation(kalkulation, menge, kalkulationsDefaults);
  };

  /** Material-EK-Gates bleiben auf den Materialmodus begrenzt; direkt-EP behält die X84-Baseline. */
  const matchBlockReason = (match: GaebPriceMatch): string | null => {
    if (mode === 'material-ek') {
      if (rundetStillAufNull(match.unitPrice)) {
        return 'Preis < 0,01 € je Einheit — für die Kalkulation zu klein.';
      }
      const position = positionById.get(match.positionId);
      if (position && materialEkImportBlockiertDurchBestandskalkulation(position.kalkulation ?? null)) {
        return 'Bereits eine Blitzkalkulation vorhanden — Material-EK hier nicht automatisch übernehmbar.';
      }
    }
    return null;
  };

  const resetState = () => {
    setParsed(null);
    setIsParsing(false);
    setIsApplying(false);
    setMode('material-ek');
    setAppliedMaterialEkIds(new Set());
    setRejectedMaterialEkIds(new Set());
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    if (lastProcessedOfferDocumentEpochRef.current === offerDocumentEpoch) return;
    lastProcessedOfferDocumentEpochRef.current = offerDocumentEpoch;
    parseGenerationRef.current += 1;
    parseAbortControllerRef.current?.abort();
    parseAbortControllerRef.current = null;
    setIsOpen(false);
    resetState();
  }, [offerDocumentEpoch]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      parseGenerationRef.current += 1;
      parseAbortControllerRef.current?.abort();
      parseAbortControllerRef.current = null;
      resetState();
    }
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    parseGenerationRef.current += 1;
    const startGeneration = parseGenerationRef.current;
    const startOfferDocumentEpoch = useOfferStore.getState().offerDocumentEpoch;
    parseAbortControllerRef.current?.abort();
    const controller = new AbortController();
    parseAbortControllerRef.current = controller;

    setIsParsing(true);
    setParsed(null);
    setAppliedMaterialEkIds(new Set());
    setRejectedMaterialEkIds(new Set());
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/gaeb/parse', {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Datei konnte nicht gelesen werden' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      const rawEntries: any[] = Array.isArray(result.entries) ? result.entries : [];

      const entries: ImportedPriceEntry[] = rawEntries
        .filter((entry) => entry?.type !== 'title')
        .map((entry) => {
          const ourPrice = typeof entry.ourPrice === 'number' ? entry.ourPrice : undefined;
          const preis = typeof entry.preis === 'number' ? entry.preis : undefined;
          const unitPrice = ourPrice && ourPrice > 0 ? ourPrice : preis;
          return {
            gaebOriginalId: entry.gaebOriginalId || undefined,
            positionNumber: entry.positionNumber || undefined,
            shortText: entry.shortText || entry.name || undefined,
            unitPrice,
          };
        });

      if (!canCommitX84ParseResult({
        startGeneration,
        currentGeneration: parseGenerationRef.current,
        startOfferDocumentEpoch,
        getCurrentOfferState: useOfferStore.getState,
      })) {
        return;
      }
      setParsed({ fileName: file.name, entries, offerDocumentEpoch: startOfferDocumentEpoch });
    } catch (error) {
      if (!canCommitX84ParseResult({
        startGeneration,
        currentGeneration: parseGenerationRef.current,
        startOfferDocumentEpoch,
        getCurrentOfferState: useOfferStore.getState,
      })) {
        return;
      }
      toast({
        title: 'Import fehlgeschlagen',
        description: error instanceof Error ? error.message : 'Datei konnte nicht gelesen werden',
        variant: 'destructive',
      });
    } finally {
      const isCurrent = canCommitX84ParseResult({
        startGeneration,
        currentGeneration: parseGenerationRef.current,
        startOfferDocumentEpoch,
        getCurrentOfferState: useOfferStore.getState,
      });
      if (parseAbortControllerRef.current === controller) {
        parseAbortControllerRef.current = null;
      }
      if (isCurrent) setIsParsing(false);
    }
  };

  const handleApply = () => {
    if (!matchResult || !parsed || matchResult.matches.length === 0) return;
    if (useOfferStore.getState().offerDocumentEpoch !== parsed.offerDocumentEpoch) {
      setIsOpen(false);
      resetState();
      return;
    }
    setIsApplying(true);
    try {
      if (mode === 'direkt-ep') {
        const result = applyX84DirectUnitPrices({
          matches: matchResult.matches,
          expectedDocumentEpoch: parsed.offerDocumentEpoch,
          getCurrentDocumentEpoch: () => useOfferStore.getState().offerDocumentEpoch,
          hasPosition: (positionId) => positionById.has(positionId),
          updatePosition: (positionId, unitPrice) => {
            updatePosition(positionId, { preis: unitPrice });
          },
        });
        if (result.documentChanged) {
          setIsOpen(false);
          resetState();
          return;
        }
        toast({
          title: result.appliedCount > 0 ? 'Preise übernommen' : 'Keine Preise übernommen',
          description: formatX84DirectApplyDescription(result),
          variant: result.appliedCount === 0 ? 'destructive' : undefined,
        });
        if (result.appliedCount > 0) {
          setIsOpen(false);
          resetState();
        }
        return;
      }

      const matchPositionIds = matchResult.matches.map((match) => match.positionId);
      const blockedPositionIds = matchResult.matches
        .filter(
          (match) =>
            !appliedMaterialEkIds.has(match.positionId) &&
            (!positionById.has(match.positionId) || matchBlockReason(match) !== null),
        )
        .map((match) => match.positionId);
      const attemptedMaterialEkIds = planX84MaterialEkAttempt({
        matchPositionIds,
        blockedPositionIds,
        appliedPositionIds: [...appliedMaterialEkIds],
      });
      const matchByPositionId = new Map(
        matchResult.matches.map((match) => [match.positionId, match]),
      );
      const materialEkApplyResults: boolean[] = [];
      const attemptedMaterialEkKalkulationen: PositionsKalkulation[] = [];
      for (const positionId of attemptedMaterialEkIds) {
        const match = matchByPositionId.get(positionId);
        const position = positionById.get(positionId);
        if (!match || !position) continue;
        const kalkulation = buildKalkulationMitMaterialEk(position, match.unitPrice, parsed.fileName);
        attemptedMaterialEkKalkulationen.push(kalkulation);
        const write = commitOfferDocumentWriteIfCurrent({
          expectedDocumentEpoch: parsed.offerDocumentEpoch,
          getCurrentDocumentEpoch: () => useOfferStore.getState().offerDocumentEpoch,
          write: () => updatePositionKalkulation(positionId, kalkulation),
        });
        if (!write.committed) {
          setIsOpen(false);
          resetState();
          return;
        }
        materialEkApplyResults.push(write.value);
      }

      const transition = transitionX84MaterialEkApply({
        matchPositionIds,
        blockedPositionIds,
        previouslyAppliedPositionIds: [...appliedMaterialEkIds],
        attemptedPositionIds: attemptedMaterialEkIds,
        applyResults: materialEkApplyResults,
      });
      const erfolgreichAngewendeteKalkulationen = selectSuccessfullyAppliedValues(
        attemptedMaterialEkKalkulationen,
        materialEkApplyResults,
      );
      const materialApplyDescription = formatX84MaterialApplyDescription(
        transition.appliedCount,
        erfolgreichAngewendeteKalkulationen,
        kalkulationsDefaults,
        {
          rejectedCount: transition.rejectedCount,
          blockedCount: transition.blockedPositionIds.length,
        },
      );
      if (!transition.shouldCloseDialog) {
        setAppliedMaterialEkIds(new Set(transition.appliedPositionIds));
        setRejectedMaterialEkIds(new Set(transition.rejectedPositionIds));
        toast({
          title:
            transition.toastKind === 'partial'
              ? 'Preise teilweise übernommen'
              : 'Keine Preise übernommen',
          description: materialApplyDescription,
          variant: 'destructive',
        });
        return;
      }

      const applied = transition.appliedCount;
      toast({
        title: applied > 0 ? 'Preise übernommen' : 'Keine Preise übernommen',
        description: materialApplyDescription,
        variant: applied === 0 ? 'destructive' : undefined,
      });
      if (applied > 0) {
        setIsOpen(false);
        resetState();
      }
    } finally {
      setIsApplying(false);
    }
  };

  const matchCount = matchResult?.matches.length ?? 0;
  const unmatchedEntryCount = matchResult?.unmatchedEntries.length ?? 0;
  const blockedMaterialEkIds =
    matchResult && mode === 'material-ek'
      ? matchResult.matches
          .filter(
            (match) =>
              !appliedMaterialEkIds.has(match.positionId) &&
              (!positionById.has(match.positionId) || matchBlockReason(match) !== null),
          )
          .map((match) => match.positionId)
      : [];
  const blockedCount = matchResult
    ? mode === 'material-ek'
      ? blockedMaterialEkIds.length
      : matchResult.matches.filter((match) => matchBlockReason(match) !== null).length
    : 0;
  const pendingMaterialEkIds =
    matchResult && mode === 'material-ek'
      ? planX84MaterialEkAttempt({
          matchPositionIds: matchResult.matches.map((match) => match.positionId),
          blockedPositionIds: blockedMaterialEkIds,
          appliedPositionIds: [...appliedMaterialEkIds],
        })
      : [];
  const applicableCount =
    mode === 'material-ek'
      ? pendingMaterialEkIds.length
      : matchCount - blockedCount;
  const materialModeKalkulationen =
    matchResult && parsed
      ? matchResult.matches.flatMap((match) => {
          const position = positionById.get(match.positionId);
          if (
            !position ||
            appliedMaterialEkIds.has(match.positionId) ||
            rundetStillAufNull(match.unitPrice) ||
            materialEkImportBlockiertDurchBestandskalkulation(position.kalkulation ?? null)
          ) {
            return [];
          }
          return [buildKalkulationMitMaterialEk(position, match.unitPrice, parsed.fileName)];
        })
      : [];
  const materialModeAufschlagHinweis = formatBatchAufschlagHinweis(
    ermittleEffektivenBatchAufschlag(materialModeKalkulationen, kalkulationsDefaults),
  );
  const materialModeAufschlagKlammer = materialModeAufschlagHinweis
    ? ` (${materialModeAufschlagHinweis})`
    : '';

  // Bewusst KEIN isGAEBDatabase-Gate (Welle 1.4 geprüft, nicht umgesetzt):
  // isGAEBDatabase wird von "In Preisliste übernehmen" zurückgesetzt
  // (store.ts convertGaebToOffer), aber die Positionen tragen im aktuellen
  // Datenmodell kein zuverlässiges "war mal GAEB"-Flag mehr (gaebOriginalId
  // wird auf den Client-Positions nirgends gesetzt — nur originalId, das
  // auch nicht-GAEB-Positionen tragen). Ein Gate auf isGAEBDatabase allein
  // hätte den bestehenden, bereits getesteten Preisimport-Workflow NACH der
  // Übernahme unsichtbar gemacht (gaeb.spec.ts). Bleibt daher unconditional
  // sichtbar wie vor dieser Änderung — echter Bedarf für ein persistentes
  // Herkunfts-Flag ist ein Folge-Ticket, kein Teil dieser UX-Welle.
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {/* Eigener Trigger nur im ungesteuerten Standalone-Modus. Im Menü
          (controlled) sitzt der Trigger als DropdownMenuItem im Parent. */}
      {!isControlled && (
        <DialogTrigger asChild>
          <Button variant="outline" className="flex items-center space-x-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50">
            <FileUp className="h-4 w-4" />
            <span>X84 Preise importieren</span>
          </Button>
        </DialogTrigger>
      )}

      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <FileUp className="h-5 w-5 text-emerald-600" />
            <span>X84-Preisrückimport vom Großhandel</span>
          </DialogTitle>
          <DialogDescription>
            GAEB-Datei mit Einheitspreisen einlesen und Preise in die Kalkulation übernehmen.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-1">
          {/* Dateiauswahl */}
          <div className="space-y-2">
            <Label htmlFor="x84-price-file">GAEB-Preisdatei (.x84, .x83, .xml, .d84)</Label>
            <input
              id="x84-price-file"
              ref={fileInputRef}
              type="file"
              accept=".x84,.x83,.xml,.d84,.p84"
              onChange={handleFileSelected}
              disabled={isParsing || isApplying}
              className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-emerald-700 hover:file:bg-emerald-100"
            />
            {isParsing && (
              <div className="flex items-center text-sm text-gray-500">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Datei wird eingelesen...
              </div>
            )}
          </div>

          {parsed && matchResult && (
            <>
              {/* Match-Statistik */}
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <Badge className="bg-emerald-600">{matchCount} zugeordnet</Badge>
                {unmatchedEntryCount > 0 && (
                  <Badge variant="secondary">{unmatchedEntryCount} Einträge ohne Zuordnung/Preis</Badge>
                )}
                {blockedCount > 0 && (
                  <Badge variant="outline" className="border-amber-300 text-amber-700">
                    {blockedCount} blockiert (siehe Hinweise)
                  </Badge>
                )}
                <span className="text-gray-500 truncate">{parsed.fileName}</span>
              </div>

              {/* Modus-Auswahl */}
              <RadioGroup value={mode} onValueChange={(value) => setMode(value as ImportMode)} className="space-y-1">
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="material-ek" id="mode-material-ek" className="mt-0.5" />
                  <Label htmlFor="mode-material-ek" className="font-normal cursor-pointer">
                    Als Material-EK in Kalkulation{materialModeAufschlagKlammer}
                    <span className="block text-xs text-gray-500">
                      Einheitspreis wird aus der Kalkulation neu berechnet.
                    </span>
                  </Label>
                </div>
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="direkt-ep" id="mode-direkt-ep" className="mt-0.5" />
                  <Label htmlFor="mode-direkt-ep" className="font-normal cursor-pointer">
                    Direkt als Einheitspreis übernehmen
                    <span className="block text-xs text-gray-500">
                      Preis wird ohne Aufschlag als EP gesetzt.
                    </span>
                  </Label>
                </div>
              </RadioGroup>

              {/* Vorschau-Tabelle */}
              {matchCount > 0 ? (
                <div className="border rounded-md overflow-hidden">
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr className="text-left text-xs text-gray-600">
                          <th className="px-2 py-1.5 font-medium">Pos-Nr</th>
                          <th className="px-2 py-1.5 font-medium">Text</th>
                          <th className="px-2 py-1.5 font-medium text-right">EK-Preis</th>
                          <th className="px-2 py-1.5 font-medium text-right">Neuer EP</th>
                          <th className="px-2 py-1.5 font-medium">Match</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matchResult.matches.map((match) => {
                          const position = positionById.get(match.positionId);
                          const alreadyApplied =
                            mode === 'material-ek' && appliedMaterialEkIds.has(match.positionId);
                          const wasRejected =
                            mode === 'material-ek' && rejectedMaterialEkIds.has(match.positionId);
                          const reason = alreadyApplied ? null : matchBlockReason(match);
                          const neuerEp = reason ? null : neuerEinheitspreis(match);
                          return (
                            <tr
                              key={match.positionId}
                              className={`border-t${
                                wasRejected
                                  ? ' bg-red-50'
                                  : alreadyApplied
                                    ? ' bg-emerald-50'
                                    : reason
                                      ? ' bg-amber-50'
                                      : ''
                              }`}
                            >
                              <td className="px-2 py-1.5 whitespace-nowrap">
                                {(position as any)?.positionNumber || '-'}
                              </td>
                              <td className="px-2 py-1.5 max-w-[220px] truncate" title={position?.name || match.entryText}>
                                {position?.name || match.entryText || '-'}
                                {reason && (
                                  <span className="flex items-center gap-1 text-[11px] text-amber-700 mt-0.5">
                                    <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                                    {reason}
                                  </span>
                                )}
                                {alreadyApplied && (
                                  <span className="block text-[11px] text-emerald-700 mt-0.5">
                                    Bereits übernommen
                                  </span>
                                )}
                                {wasRejected && (
                                  <span className="flex items-center gap-1 text-[11px] text-red-700 mt-0.5">
                                    <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                                    Übernahme abgelehnt — bitte einzeln prüfen
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap">{formatEuro(match.unitPrice)}</td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap font-medium">
                                {reason ? 'blockiert' : neuerEp !== null ? formatEuro(neuerEp) : '-'}
                              </td>
                              <td className="px-2 py-1.5">
                                <Badge variant="outline" className="text-[10px]">
                                  {match.matchedBy === 'id' ? 'GAEB-ID' : 'Pos-Nr'}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                  Keine Positionen konnten zugeordnet werden. Bitte prüfen, ob die Datei zu diesem LV gehört.
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isApplying}>
            Abbrechen
          </Button>
          <Button
            onClick={handleApply}
            disabled={!matchResult || applicableCount === 0 || isApplying || isParsing}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isApplying ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Übernehme...</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" />{applicableCount} Preis(e) übernehmen</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
